import classNames from 'classnames'
import { KeyboardEvent } from 'react'
import { useTranslation } from 'react-i18next'

interface FilterPriceRangeProps {
  priceMin?: number
  priceMax?: number
  onChange: (min: number | undefined, max: number | undefined) => void
  /** Larger tap targets for mobile filter sheet */
  compact?: boolean
}

/** Parse user input into a non-negative finite number, or undefined if empty/invalid. */
export function parsePositivePriceInput(raw: string): number | undefined {
  const trimmed = raw.trim()
  if (trimmed === '') return undefined

  // Digits only (no signs). Optional decimals for cents.
  if (!/^\d+(\.\d{0,2})?$/.test(trimmed)) return undefined
  if (trimmed.endsWith('.')) return undefined

  const value = Number(trimmed)
  if (!Number.isFinite(value) || value < 0) return undefined
  return value
}

export function isPriceRangeValid(
  priceMin?: number,
  priceMax?: number
): boolean {
  const hasMin = priceMin !== undefined && priceMin !== null && Number.isFinite(priceMin)
  const hasMax = priceMax !== undefined && priceMax !== null && Number.isFinite(priceMax)

  if (hasMin && priceMin! < 0) return false
  if (hasMax && priceMax! < 0) return false
  if (hasMin && hasMax && priceMin! > priceMax!) return false
  return true
}

function blockNonPositiveKeys(e: KeyboardEvent<HTMLInputElement>) {
  if (e.key === '-' || e.key === 'e' || e.key === 'E' || e.key === '+') {
    e.preventDefault()
  }
}

const FilterPriceRange = ({
  priceMin,
  priceMax,
  onChange,
  compact = false,
}: FilterPriceRangeProps) => {
  const { t } = useTranslation()
  const invalidRange =
    priceMin !== undefined &&
    priceMax !== undefined &&
    Number.isFinite(priceMin) &&
    Number.isFinite(priceMax) &&
    priceMin > priceMax

  const inputClass = classNames(
    'min-w-0 flex-1 basis-0 rounded-lg border bg-white text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-1',
    compact ? 'min-h-[44px] px-3 py-2.5 text-base' : 'px-2 py-2 text-sm',
    invalidRange
      ? 'border-red-400 focus:border-red-500 focus:ring-red-400/40'
      : 'border-gray-300 focus:border-imovPrimary focus:ring-imovPrimary'
  )

  const handleMinChange = (raw: string) => {
    if (raw === '') {
      onChange(undefined, priceMax)
      return
    }
    const parsed = parsePositivePriceInput(raw)
    if (parsed === undefined) return
    onChange(parsed, priceMax)
  }

  const handleMaxChange = (raw: string) => {
    if (raw === '') {
      onChange(priceMin, undefined)
      return
    }
    const parsed = parsePositivePriceInput(raw)
    if (parsed === undefined) return
    onChange(priceMin, parsed)
  }

  return (
    <div className="w-full min-w-0 space-y-1.5">
      <div className="flex w-full min-w-0 items-center gap-1.5 sm:gap-2">
        <input
          type="number"
          inputMode="decimal"
          placeholder={t('filters.priceMinPlaceholder')}
          min={0}
          step={1}
          value={priceMin ?? ''}
          onKeyDown={blockNonPositiveKeys}
          onChange={e => handleMinChange(e.target.value)}
          onBlur={() => {
            if (priceMin !== undefined && priceMin < 0) {
              onChange(undefined, priceMax)
            }
          }}
          aria-invalid={invalidRange}
          aria-describedby={invalidRange ? 'price-range-error' : undefined}
          className={inputClass}
        />
        <span className="shrink-0 text-gray-400 font-medium" aria-hidden>
          —
        </span>
        <input
          type="number"
          inputMode="decimal"
          placeholder={t('filters.priceMaxPlaceholder')}
          min={0}
          step={1}
          value={priceMax ?? ''}
          onKeyDown={blockNonPositiveKeys}
          onChange={e => handleMaxChange(e.target.value)}
          onBlur={() => {
            if (priceMax !== undefined && priceMax < 0) {
              onChange(priceMin, undefined)
            }
          }}
          aria-invalid={invalidRange}
          aria-describedby={invalidRange ? 'price-range-error' : undefined}
          className={inputClass}
        />
      </div>
      {invalidRange ? (
        <p id="price-range-error" className="text-xs text-red-600" role="alert">
          {t('filters.priceRangeInvalid')}
        </p>
      ) : null}
    </div>
  )
}

export default FilterPriceRange
